/**
 * Shared DOM range state and lifecycle operations.
 *
 * Marker binding and node reconciliation both operate on the same bounded
 * range. Housing that state here keeps the two implementations acyclic:
 * marker discovery creates ranges, while node reconciliation mutates their
 * contents. Cleanup remains centralized so nested templates, repeats, event
 * parts, and text nodes release resources in the same order. `cacheMap` and
 * `activeCacheTemplate` back `cache()` (G13): switching between previously
 * seen templates detaches and re-inserts nodes instead of destroying the
 * instance. `asyncEpoch`/`untilIndex`/`destroyed` back async node values and
 * `until()` (G7): every part update bumps the epoch so a promise settling
 * after the part moved on is provably stale, and priority ordering for
 * `until()` is tracked per part.
 */
import { disposeOwner } from './owner.js';
/**
 * Depth of the enclosing "an ancestor of this subtree is being detached"
 * regions (#1311). React's deletion walk carries the same fact as a
 * `hostParent` it sets to `null` once it has removed a host node, so the
 * descendants it keeps visiting for their cleanup effects never issue a DOM
 * removal of their own — only the topmost node of a deleted subtree costs a
 * mutation. Hydrogen has no single deletion walk to thread a parameter
 * through: a repeat entry's teardown reaches nested parts and nested
 * instances through `Materialized.cleanup` closures and `TemplateInstance`
 * ABI methods, neither of which can carry it. A counter is the same
 * information in the same synchronous window.
 *
 * The contract for entering: the caller MUST remove every node of the subtree
 * it is disposing. It is not "skip DOM work", it is "I am removing the roots,
 * so you do not have to remove what hangs off them". A cleanup that unmounts
 * some unrelated live instance from inside the window would have its DOM
 * removal skipped too — the same exposure React's `hostParent` has, and the
 * reason the window stays wrapped around teardown alone.
 */
let subtreeDetachDepth = 0;
/** True while an ancestor detach makes an individual node removal redundant. */
export function isSubtreeDetaching() {
    return subtreeDetachDepth > 0;
}
/**
 * Runs `teardown` under the ancestor-detach fact above. The caller must remove
 * every root node of the disposed subtree itself, before or after `teardown`.
 */
export function withSubtreeDetach(teardown) {
    subtreeDetachDepth += 1;
    try {
        return teardown();
    }
    finally {
        subtreeDetachDepth -= 1;
    }
}
/**
 * The element holding this part's range, or null when the part is unbound.
 *
 * One accessor rather than `part.end?.parentNode` spelled out at each call
 * site, because a range-parented part (#1354) has no `end` to read it from.
 */
export function rangeParentOf(part) {
    return part.rangeParent ?? part.end?.parentNode ?? null;
}
/** The first node inside a part's range, or null while the range is empty. */
export function rangeFirstNode(part) {
    return part.rangeParent ? part.rangeParent.firstChild : (part.anchor?.nextSibling ?? null);
}
export function removeRange(part) {
    const owned = part.rangeParent;
    if (!owned && (!part.anchor || !part.end))
        return;
    if (isSubtreeDetaching()) {
        // An ancestor is being detached: these nodes leave with it.
        part.nodes = [];
        return;
    }
    if (owned) {
        // The part owns every child, so the whole range leaves in one call — the
        // bulk shape `emptyRangeAtOnce` has to earn with a walk when the range is
        // only part of its parent's children. Through the adapter, because a DOM
        // without `replaceChildren` still has to empty it.
        part.domAdapter?.replaceChildren(owned, []);
        part.nodes = [];
        return;
    }
    let node = part.anchor?.nextSibling ?? null;
    while (node && node !== part.end) {
        const next = node.nextSibling;
        node.parentNode?.removeChild(node);
        node = next;
    }
    part.nodes = [];
}
/** Text and Comment nodes carry no state that re-insertion into the DOM resets. */
function isStatelessNode(node) {
    return node.nodeType === 3 || node.nodeType === 8;
}
/**
 * Empties the part's whole range in one DOM call instead of one per node.
 *
 * `replaceChildren` on the parent is a single bulk removal — React reaches for
 * the same shape (`setTextContent`) whenever a host parent's children are
 * fully replaced — but it re-appends whatever it is given, and re-inserting an
 * element resets focus, selection, scroll offset, media playback and iframe
 * documents. So the parent's children outside the range must all be Text or
 * Comment nodes, which survive re-insertion unobserved; the walk stops at the
 * first element and falls back to `removeRange`. Boundary comments are kept in
 * place, so the part is still bound to its range afterwards.
 *
 * Worth calling only when the range is large: the fallback walks the parent's
 * out-of-range children, which costs more than removing one or two nodes.
 */
export function emptyRangeAtOnce(part) {
    // A range-parented part (#1354) has nothing to walk: every child is in
    // range and there are no boundary comments to keep, so `removeRange` is
    // already the single bulk call this function exists to reach.
    if (part.rangeParent) {
        removeRange(part);
        return;
    }
    const { anchor, end, domAdapter } = part;
    const parent = anchor?.parentNode;
    if (!anchor || !end || !parent || !domAdapter || end.parentNode !== parent) {
        removeRange(part);
        return;
    }
    // A sibling-bounded hole (#1390) is bounded by static neighbour nodes, not
    // by its own comments, and those neighbours are usually elements: handing
    // them to `replaceChildren` re-inserts them and resets focus, selection and
    // scroll exactly as the walk below refuses to for every other child.
    if (!isStatelessNode(anchor) || !isStatelessNode(end)) {
        removeRange(part);
        return;
    }
    const keep = [];
    for (let node = parent.firstChild; node && node !== anchor; node = node.nextSibling) {
        if (!isStatelessNode(node)) {
            removeRange(part);
            return;
        }
        keep.push(node);
    }
    keep.push(anchor, end);
    for (let node = end.nextSibling; node; node = node.nextSibling) {
        if (!isStatelessNode(node)) {
            removeRange(part);
            return;
        }
        keep.push(node);
    }
    domAdapter.replaceChildren(parent, keep);
    part.nodes = [];
}
export function insertBeforeEnd(part, nodes) {
    // `end` is the node the range stops before; a range-parented part (#1354)
    // stops at the parent's end, and `insertBefore(node, null)` appends, which
    // is the same instruction spelled without a marker to point at.
    //
    // An unbound part is skipped by the optional call rather than by a guard:
    // there is no branch, so there is no way for one to fall through.
    const parent = rangeParentOf(part);
    const end = part.end ?? null;
    for (const node of nodes) {
        parent?.insertBefore(node, end);
    }
}
/**
 * Spends the private fragment a materialization was mounted into, once its
 * nodes are where they belong.
 *
 * Placement is what makes that fragment worthless, and worthless is not the
 * same as unreferenced: the materialization held it and so did the instance,
 * as `container`. A 1,000-row list keeps 1,000 drained fragments that way, and
 * for a consumed parse (#1375) each one drags its host `<template>` and their
 * contents-owner document along with it (#1335). `container` follows the nodes
 * instead; `mountEpoch` is what proves ownership now.
 *
 * `parent` is passed in rather than read back off the nodes: every caller has
 * just inserted into it, and `node.parentNode` is a DOM accessor — a thousand
 * of them per keyed list, in the coldest JIT tier a page has.
 *
 * A shared function rather than a per-materialization closure: this runs once
 * per placed row.
 */
export function releasePlacedFragment(materialized, parent) {
    materialized.fragment = undefined;
    materialized.instance?.adoptPlacement?.(parent);
    // A composite's children were placed into the same parent as its own
    // flattened node list, so they are spent by the same insertion.
    const children = materialized.children;
    if (!children)
        return;
    for (const child of children)
        releasePlacedFragment(child, parent);
}
export function destroyMaterialized(materialized) {
    if (typeof materialized?.cleanup === 'function') {
        materialized.cleanup();
    }
}
/**
 * Retires a keyed-repeat entry: its DOM and instance first, then the row's own
 * reactive scope (#1310), so teardown runs inner-to-outer — the row instance is
 * a child of that scope. Every path that drops an entry goes through here, so a
 * row's computeds and cleanups are released exactly once, when the row leaves.
 */
export function destroyRepeatEntry(entry) {
    destroyMaterialized(entry.materialized);
    if (entry.scope) {
        disposeOwner(entry.scope);
        entry.scope = undefined;
    }
}
/**
 * Unmounts an instance only while this materialization still owns it (#626).
 * A pre-built instance can be re-mounted under a new parent before the old
 * parent's teardown runs; the stale cleanup must not tear it out of its new
 * home.
 *
 * Ownership is proved by the mount identity the materialization captured:
 * every bind stamps a fresh `mountEpoch` and unmount clears it, so an epoch
 * that still matches means this exact mount is still live (#1335). A caller
 * that has no epoch — and an instance implementation that does not keep one —
 * falls back to `container` still pointing at what it mounted into, which is
 * what proved it before; an implementation without either preserves the
 * historical unconditional unmount.
 */
export function unmountInstanceIfOwned(instance, owner, epoch) {
    if (epoch !== undefined) {
        if (instance.mountEpoch !== epoch)
            return;
    }
    else if (instance.container !== undefined && instance.container !== owner) {
        return;
    }
    instance.unmount();
}
//# sourceMappingURL=render-dom-range.js.map